import React from 'react'
import { motion } from 'framer-motion'
import { 
  Key,
  Clock,
  Shield,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Info
} from 'lucide-react'
import { gameUIVariants } from '@/lib/animations'

interface LicenseManagerProps {
  cosmkId: string
  drmState: {
    status: 'initializing' | 'ready' | 'error' | 'blocked'
    supportedSystems: string[]
    activeDRM?: string
    error?: string
  }
  licenseInfo?: {
    licenseId: string
    drmSystem: string
    issuedAt: string
    expiresAt: string
    permissions: any
    forensicId: string
  }
  userInfo?: {
    userId: string
    sessionId: string
  }
}

export const LicenseManager: React.FC<LicenseManagerProps> = ({
  cosmkId,
  drmState,
  licenseInfo,
  userInfo
}) => {
  const [licenseStatus, setLicenseStatus] = React.useState<'pending' | 'active' | 'expired' | 'revoked'>('pending')
  const [timeRemaining, setTimeRemaining] = React.useState<string>('')
  const [showDetails, setShowDetails] = React.useState(false)

  // Update license status and time remaining
  React.useEffect(() => {
    if (licenseInfo) {
      const now = new Date().getTime()
      const expiresAt = new Date(licenseInfo.expiresAt).getTime()
      
      if (now < expiresAt) {
        setLicenseStatus('active')
        
        // Update countdown every second
        const interval = setInterval(() => {
          const timeLeft = expiresAt - new Date().getTime()
          if (timeLeft > 0) {
            setTimeRemaining(formatTimeRemaining(timeLeft))
          } else {
            setLicenseStatus('expired')
            clearInterval(interval)
          }
        }, 1000)

        return () => clearInterval(interval)
      } else {
        setLicenseStatus('expired')
      }
    }
  }, [licenseInfo])

  const formatTimeRemaining = (milliseconds: number): string => {
    const seconds = Math.floor(milliseconds / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (days > 0) {
      return `${days}d ${hours % 24}h ${minutes % 60}m`
    } else if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`
    } else {
      return `${seconds}s`
    }
  }

  const getStatusIcon = () => {
    switch (licenseStatus) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-400" />
      case 'expired':
        return <XCircle className="h-4 w-4 text-red-400" />
      case 'revoked':
        return <XCircle className="h-4 w-4 text-red-400" />
      default:
        return <Clock className="h-4 w-4 text-yellow-400 animate-pulse" />
    }
  }

  const getStatusColor = () => {
    switch (licenseStatus) {
      case 'active':
        return 'text-green-400'
      case 'expired':
        return 'text-red-400'
      case 'revoked':
        return 'text-red-400'
      default:
        return 'text-yellow-400'
    }
  }

  const handleRefreshLicense = async () => {
    try {
      // In production, make API call to refresh license
      console.log('Refreshing license for COSMK ID:', cosmkId)
      
      const response = await fetch('/api/drm/license/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cosmkId,
          licenseId: licenseInfo?.licenseId,
          userInfo
        })
      })

      if (!response.ok) {
        throw new Error('License refresh failed')
      }

      const newLicenseInfo = await response.json()
      console.log('License refreshed successfully:', newLicenseInfo)

    } catch (error) {
      console.error('License refresh failed:', error)
    }
  }

  if (!licenseInfo) {
    return (
      <div className="p-3 bg-cosmic-dark/30 rounded-lg border border-stellar-cyan/20 mb-4">
        <div className="flex items-center space-x-2">
          <Clock className="h-4 w-4 text-yellow-400 animate-pulse" />
          <span className="text-sm text-stellar-cyan">Acquiring DRM license...</span>
        </div>
      </div>
    )
  }

  return (
    <motion.div 
      className="p-4 bg-cosmic-dark/30 rounded-lg border border-stellar-cyan/20 mb-6"
      variants={gameUIVariants}
      initial="elementEnter"
      animate="elementVisible"
    >
      {/* License Status Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-3">
          <Key className="h-5 w-5 text-stellar-cyan" />
          <div>
            <h4 className="text-sm font-semibold text-white">DRM License</h4>
            <p className="text-xs text-stellar-cyan/60">
              {licenseInfo.drmSystem.toUpperCase()} • ID: {licenseInfo.licenseId.substring(0, 8)}...
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-1">
            {getStatusIcon()}
            <span className={`text-sm font-medium ${getStatusColor()}`}>
              {licenseStatus.toUpperCase()}
            </span>
          </div>
          
          {licenseStatus === 'expired' && (
            <motion.button
              onClick={handleRefreshLicense}
              className="p-1 text-stellar-cyan hover:text-white transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title="Refresh License"
            >
              <RefreshCw className="h-4 w-4" />
            </motion.button>
          )}
          
          <motion.button
            onClick={() => setShowDetails(!showDetails)}
            className="p-1 text-stellar-cyan hover:text-white transition-colors"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            title="Show Details"
          >
            <Info className="h-4 w-4" />
          </motion.button>
        </div>
      </div>

      {/* License Status Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
        <div className="text-center md:text-left">
          <p className="text-xs text-stellar-cyan/60 font-mono">Time Remaining</p>
          <p className="text-sm font-semibold text-white font-mono">
            {licenseStatus === 'active' ? timeRemaining : 'Expired'}
          </p>
        </div>
        
        <div className="text-center">
          <p className="text-xs text-stellar-cyan/60 font-mono">DRM System</p>
          <p className="text-sm font-semibold text-white">
            {licenseInfo.drmSystem.toUpperCase()}
          </p>
        </div>
        
        <div className="text-center md:text-right">
          <p className="text-xs text-stellar-cyan/60 font-mono">Permissions</p>
          <div className="flex justify-center md:justify-end space-x-1 mt-1">
            {licenseInfo.permissions.play && (
              <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded">PLAY</span>
            )}
            {licenseInfo.permissions.pause && (
              <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded">PAUSE</span>
            )}
            {licenseInfo.permissions.seek && (
              <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded">SEEK</span>
            )}
          </div>
        </div>
      </div>

      {/* Security Warning for Expired License */}
      {licenseStatus === 'expired' && (
        <motion.div 
          className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg mb-3"
          variants={gameUIVariants}
          initial="elementEnter"
          animate="elementVisible"
        >
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-4 w-4 text-red-400" />
            <p className="text-red-400 text-sm">
              License has expired. Content playback is blocked for security.
            </p>
          </div>
        </motion.div>
      )}

      {/* Detailed License Information */}
      {showDetails && (
        <motion.div 
          className="mt-4 p-3 bg-cosmic-dark/50 rounded-lg border border-stellar-cyan/10"
          variants={gameUIVariants}
          initial="elementEnter"
          animate="elementVisible"
        >
          <h5 className="text-sm font-semibold text-white mb-3 flex items-center">
            <Shield className="h-4 w-4 mr-2" />
            License Details
          </h5>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs font-mono">
            <div>
              <p className="text-stellar-cyan/60">License ID:</p>
              <p className="text-white break-all">{licenseInfo.licenseId}</p>
            </div>
            
            <div>
              <p className="text-stellar-cyan/60">Forensic ID:</p>
              <p className="text-white break-all">{licenseInfo.forensicId}</p>
            </div>
            
            <div>
              <p className="text-stellar-cyan/60">Issued At:</p>
              <p className="text-white">{new Date(licenseInfo.issuedAt).toLocaleString()}</p>
            </div>
            
            <div>
              <p className="text-stellar-cyan/60">Expires At:</p>
              <p className="text-white">{new Date(licenseInfo.expiresAt).toLocaleString()}</p>
            </div>
            
            <div className="md:col-span-2">
              <p className="text-stellar-cyan/60">Content ID:</p>
              <p className="text-white break-all">{cosmkId}</p>
            </div>
            
            {userInfo && (
              <>
                <div>
                  <p className="text-stellar-cyan/60">User ID:</p>
                  <p className="text-white">{userInfo.userId}</p>
                </div>
                
                <div>
                  <p className="text-stellar-cyan/60">Session ID:</p>
                  <p className="text-white break-all">{userInfo.sessionId}</p>
                </div>
              </>
            )}
          </div>
          
          <div className="mt-4 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded">
            <p className="text-yellow-400 text-xs">
              <strong>Security Notice:</strong> This license contains forensic watermarking data 
              that uniquely identifies your session. Unauthorized redistribution can be traced.
            </p>
          </div>
        </motion.div>
      )}
    </motion.div>
  )
}